import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FourGuidesSection = () => {
  const [hoveredGuide, setHoveredGuide] = useState(null);

  const handleGuideClick = () => {
    window.open('https://depthme.app', '_blank', 'noopener,noreferrer');
  };

  const guides = [
    {
      id: 'morning',
      icon: 'Sun',
      title: 'Morning Intentions',
      description: 'Start your day aligned with empowering guidance and affirmations that support your real-life situation. Journaling helps clarify your intentions and strengthens your focus.',
      color: 'from-yellow-500/20 to-orange-500/20',
      glowColor: 'shadow-yellow-500/50'
    },
    {
      id: 'shadow',
      icon: 'Mask',
      title: 'Shadow Work',
      description: 'Explore emotional patterns and triggers to uncover hidden beliefs and unconscious habits. Writing honestly helps surface what\'s hidden and transforms discomfort into awareness.',
      color: 'from-purple-500/20 to-indigo-500/20',
      glowColor: 'shadow-purple-500/50'
    },
    {
      id: 'vision',
      icon: 'Eye',
      title: 'Vision Quest',
      description: 'Practice gratitude as if your goals are already unfolding, and connect with your future self. Journaling reinforces belief, embodiment, and trust in what you\'re becoming.',
      color: 'from-blue-500/20 to-cyan-500/20',
      glowColor: 'shadow-blue-500/50'
    },
    {
      id: 'night',
      icon: 'Moon',
      title: 'Night Reflections',
      description: 'Close your day with clarity, integration, and emotional release before rest. Reflecting in your journal helps your mind process the day and release emotional weight.',
      color: 'from-indigo-500/20 to-purple-500/20',
      glowColor: 'shadow-indigo-500/50'
    }
  ];

  return (
    <section id="four-guides" className="relative py-24 px-6 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 cosmic-gradient opacity-30" />
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground">
            Four daily guides for your{' '}
            <span className="text-gradient">inner world</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Use all four or choose the ones that match your current season.
          </p>
        </div>

        {/* Guides Grid - 2x2 */}
        <div className="grid md:grid-cols-2 gap-8">
          {guides?.map((guide) => (
            <div
              key={guide?.id}
              role="button"
              tabIndex={0}
              className={`relative glassmorphism rounded-2xl p-8 transition-all duration-300 cursor-pointer
                ${hoveredGuide === guide?.id ? `${guide?.glowColor} scale-105` : 'hover:scale-105'}
              `}
              onClick={handleGuideClick}
              onKeyDown={(e) => e.key === 'Enter' && handleGuideClick()}
              onMouseEnter={() => setHoveredGuide(guide?.id)}
              onMouseLeave={() => setHoveredGuide(null)}
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${guide?.color} rounded-2xl opacity-0 transition-opacity duration-300 ${hoveredGuide === guide?.id ? 'opacity-100' : ''}`} />
              
              <div className="relative z-10 space-y-4">
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full glassmorphism">
                  <Icon 
                    name={guide?.icon} 
                    size={32} 
                    className="text-primary"
                  />
                </div>

                {/* Content */}
                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-3">
                    {guide?.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {guide?.description}
                  </p>
                </div>

                {/* Hover Indicator */}
                <div className={`flex items-center space-x-2 text-primary transition-all duration-300 ${hoveredGuide === guide?.id ? 'translate-x-2' : ''}`}>
                  <span className="text-sm font-medium">Open the app to start</span>
                  <Icon name="ArrowRight" size={16} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FourGuidesSection;